// =============================================================================
// Investor Dashboard Page - Professional Investor Portal Design
// =============================================================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  BanknotesIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  ChartBarIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  EyeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { Card, MetricCard, DocumentViewerModal, DealImage } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { investorService, type InvestmentListItem, type DocumentItem } from '../../services/investor.service';
import type { DashboardStats, Announcement } from '../../types';
import { formatDate } from '../../utils/dateFormat';
import clsx from 'clsx';

// =============================================================================
// Page Header Component
// =============================================================================

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">{title}</h1>
        {subtitle && (
          <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-3">{actions}</div>}
    </div>
  );
}

// =============================================================================
// Investment Card Component
// =============================================================================

interface InvestmentCardProps {
  investment: InvestmentListItem;
  formatCurrency: (amount: number) => string;
}

function InvestmentCard({ investment, formatCurrency }: InvestmentCardProps) {
  const propertyTypeLabels: Record<string, string> = {
    MULTIFAMILY: 'Multifamily',
    OFFICE: 'Office',
    RETAIL: 'Retail',
    INDUSTRIAL: 'Industrial',
    MIXED_USE: 'Mixed Use',
    OTHER: 'Other',
  };

  return (
    <Card hover padding="none" className="overflow-hidden">
      <Link to={`/investments/${investment.deal.id}`} className="block">
        {/* Property Image - Using proxy to bypass browser restrictions */}
        <div className="h-32 overflow-hidden">
          <DealImage
            dealId={investment.deal.id}
            dealName={investment.deal.name}
            imageUrl={investment.deal.imageUrl}
            className="w-full h-full object-cover"
            iconClassName="h-12 w-12 text-primary-300"
            fallbackClassName="h-full w-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-50"
          />
        </div>

        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700 mb-2">
                {propertyTypeLabels[investment.deal.propertyType] || investment.deal.propertyType}
              </span>
              <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                {investment.deal.name}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {investment.deal.city}, {investment.deal.state}
              </p>
            </div>
            <ArrowRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
          </div>

          {/* Investment Entity */}
          {investment.profile.entityName && (
            <p className="text-xs text-primary-600 mb-4 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
              {investment.profile.entityName}
            </p>
          )}

          {/* Metrics Grid */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Invested</p>
              <p className="text-sm font-semibold text-gray-900 mt-1">
                {formatCurrency(investment.investmentAmount)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Ownership</p>
              <p className="text-sm font-semibold text-gray-900 mt-1">
                {investment.ownershipPercentage.toFixed(2)}%
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Distributed</p>
              <p className="text-sm font-semibold text-success-600 mt-1">
                {formatCurrency(investment.totalDistributed)}
              </p>
            </div>
          </div>
        </div>
      </Link>
    </Card>
  );
}

// =============================================================================
// Component
// =============================================================================

export function DashboardPage() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [investments, setInvestments] = useState<InvestmentListItem[]>([]);
  const [recentDocuments, setRecentDocuments] = useState<DocumentItem[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [viewingDocument, setViewingDocument] = useState<DocumentItem | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Handle document download
  const handleDownload = async (doc: DocumentItem, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDownloadingId(doc.id);
    try {
      const blob = await investorService.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
      alert('Failed to download document. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  // Handle document view
  const handleView = (doc: DocumentItem, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setViewingDocument(doc);
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch all data in parallel
      const [dashboardStats, investmentsList, documentsList] = await Promise.all([
        investorService.getDashboard(),
        investorService.getInvestments(),
        investorService.getDocuments(),
      ]);

      setStats(dashboardStats);
      setInvestments(investmentsList);
      // Get the 3 most recent documents
      setRecentDocuments(documentsList.slice(0, 3));

      // Try to fetch announcements (may not be implemented yet)
      try {
        const announcementsList = await investorService.getAnnouncements();
        setAnnouncements(announcementsList.slice(0, 3));
      } catch {
        // Announcements endpoint may not exist yet, use empty array
        setAnnouncements([]);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Using centralized PST date formatting from utils/dateFormat

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-gray-200 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="h-64 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900">{error}</h3>
        <button
          onClick={loadDashboardData}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <PageHeader
        title={`Welcome back, ${firstName}`}
        subtitle="Here's an overview of your investment portfolio"
      />

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          label="Total Invested"
          value={formatCurrency(stats?.totalInvested || 0)}
          icon={<ChartBarIcon className="h-5 w-5 text-primary-600" />}
        />
        <MetricCard
          label="Total Distributed"
          value={formatCurrency(stats?.totalDistributed || 0)}
          icon={<BanknotesIcon className="h-5 w-5 text-success-600" />}
        />
        <MetricCard
          label="Active Investments"
          value={stats?.dealCount || 0}
          icon={<BuildingOfficeIcon className="h-5 w-5 text-primary-600" />}
        />
        <MetricCard
          label="Avg. Annualized Cash Yield"
          value={`${stats?.avgCashYield?.toFixed(1) || '0.0'}%`}
          icon={<ArrowTrendingUpIcon className="h-5 w-5 text-success-600" />}
        />
      </div>

      {/* Investments Section */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Your Investments</h2>
          {investments.length > 0 && (
            <Link
              to="/investments"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 transition-colors"
            >
              View all
              <ArrowRightIcon className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>

        {investments.length === 0 ? (
          <Card padding="lg">
            <div className="text-center py-8">
              <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No Investments Yet</h3>
              <p className="mt-2 text-sm text-gray-500">
                Your investment portfolio will appear here once you have active investments.
              </p>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {investments.map((investment) => (
              <InvestmentCard
                key={`${investment.deal.id}-${investment.profile.id}`}
                investment={investment}
                formatCurrency={formatCurrency}
              />
            ))}
          </div>
        )}
      </section>

      {/* Bottom Grid: Documents & Announcements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Documents */}
        <Card padding="none">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Recent Documents</h2>
              {recentDocuments.length > 0 && (
                <Link
                  to="/documents"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  View all
                </Link>
              )}
            </div>
          </div>

          {recentDocuments.length === 0 ? (
            <div className="p-8 text-center">
              <DocumentTextIcon className="h-10 w-10 text-gray-400 mx-auto" />
              <p className="mt-2 text-sm text-gray-500">No documents available</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recentDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-shrink-0 p-2.5 bg-gray-100 rounded-lg">
                    <DocumentTextIcon className="h-5 w-5 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {doc.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <CalendarIcon className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {formatDate(doc.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => handleView(doc, e)}
                      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="View document"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => handleDownload(doc, e)}
                      disabled={downloadingId === doc.id}
                      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Download document"
                    >
                      {downloadingId === doc.id ? (
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Announcements */}
        <Card padding="none">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Announcements</h2>
              {announcements.length > 0 && (
                <Link
                  to="/announcements"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  View all
                </Link>
              )}
            </div>
          </div>

          {announcements.length === 0 ? (
            <div className="p-8 text-center">
              <BanknotesIcon className="h-10 w-10 text-gray-400 mx-auto" />
              <p className="mt-2 text-sm text-gray-500">No announcements yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {announcements.map((announcement, index) => (
                <div
                  key={announcement.id}
                  className={clsx(
                    'p-4 hover:bg-gray-50 transition-colors cursor-pointer',
                    index === 0 && 'bg-primary-50/30'
                  )}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {index === 0 && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
                        New
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      {formatDate(announcement.sentAt)}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    {announcement.subject}
                  </p>
                  <p className="text-sm text-gray-500 line-clamp-2">
                    {announcement.body}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewerModal
          isOpen={!!viewingDocument}
          onClose={() => setViewingDocument(null)}
          documentId={viewingDocument.id}
          fileName={viewingDocument.name}
          mimeType={viewingDocument.mimeType}
          getEmbedUrls={investorService.getDocumentViewUrl}
        />
      )}
    </div>
  );
}

export default DashboardPage;
